import { useEffect, useState } from 'react';
import { CURRENCIES, MAX_AMOUNT_MINOR, type InvitationDraft } from '@debtbot/shared';
import { useLocale, useT } from '../i18n';
import { haptic } from '../telegram/haptic';
import { AmountInput, digitsToMinor } from './AmountInput';
import { Button } from './Button';
import { Segmented } from './Segmented';

type Payer = 'me' | 'peer';

/** Единственная валюта MVP — селектор не рисуем, показываем суффикс. */
const CURRENCY = 'UZS';

/**
 * Короткая форма первой записи перед шарингом приглашения: кто платил, сумма, заметка.
 * «Отправить» — приглашение с черновиком, «Пропустить» — обычное приглашение.
 *
 * Полноэкранный оверлей, а не нижняя шторка: поле суммы в фокусе с первого кадра, и когда
 * iOS сжимает viewport под клавиатуру, контенту, прижатому к верху, двигаться некуда —
 * шторка в такой момент «подпрыгивала». Каждое открытие — чистая форма (монтируется по open).
 */
export function InviteDraftScreen({
  open,
  pending,
  onSend,
  onSkip,
  onClose,
}: {
  open: boolean;
  pending: boolean;
  onSend: (draft: InvitationDraft) => void;
  onSkip: () => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return <DraftForm pending={pending} onSend={onSend} onSkip={onSkip} onClose={onClose} />;
}

function DraftForm({
  pending,
  onSend,
  onSkip,
  onClose,
}: {
  pending: boolean;
  onSend: (draft: InvitationDraft) => void;
  onSkip: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const locale = useLocale();
  const [payer, setPayer] = useState<Payer>('me');
  const [digits, setDigits] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const amount = digitsToMinor(digits, CURRENCY);
  const valid = amount >= 1 && amount <= MAX_AMOUNT_MINOR;
  const warning = amount > MAX_AMOUNT_MINOR ? t.form.maxAmount : null;

  const send = () => {
    if (!valid || pending) return;
    haptic.tap();
    onSend({
      amount,
      currency: CURRENCY,
      payer: payer === 'me' ? 'inviter' : 'invitee',
      note: note.trim() || null,
    });
  };

  return (
    <div
      role="dialog"
      aria-label={t.home.draftTitle}
      data-testid="invite-draft"
      className="fixed inset-0 z-40 flex flex-col gap-4 overflow-y-auto bg-bg px-4 py-4 text-text"
    >
      <header className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">{t.home.draftTitle}</h1>
        <button
          type="button"
          aria-label={t.common.cancel}
          className="rounded-full p-2 text-xl active:opacity-60"
          onClick={onClose}
        >
          ✕
        </button>
      </header>
      <div className="text-sm text-hint">{t.home.draftWhoPaid}</div>
      <Segmented<Payer>
        value={payer}
        onChange={setPayer}
        options={[
          { value: 'me', label: t.home.draftMe },
          { value: 'peer', label: t.home.draftPeer },
        ]}
      />
      <AmountInput
        digits={digits}
        onChange={setDigits}
        suffix={CURRENCIES[CURRENCY].suffix[locale]}
        warning={warning}
      />
      <input
        data-testid="draft-note-input"
        value={note}
        onChange={(e) => setNote(e.target.value.slice(0, 100))}
        placeholder={t.form.note}
        className="rounded-xl bg-secondary-bg px-4 py-3 outline-none placeholder:text-hint"
      />
      <Button data-testid="draft-send" disabled={!valid || pending} onClick={send}>
        {t.home.draftSend}
      </Button>
      <Button data-testid="draft-skip" variant="secondary" disabled={pending} onClick={onSkip}>
        {t.home.draftSkip}
      </Button>
    </div>
  );
}
